import { FlightViewMapLibMap } from '../map'
import { FISProps, FlightData, InitProps } from '../types'
import { getFlight } from './api'
import { getStatusLabel, translateStatus } from '../tracker/utils'
import { format } from 'date-fns'
import { getAircraftName, sleep } from '../utils'
import { Flight } from '../models/flight'
import { AirportLayer } from '../models/airport-layer'
import { t } from 'i18next'

const defaultTemplate = `
<template id="fvlib-fis-template">
  <div class="fvlib-fis-component">
    <div id="fvlib-fis-header">
      <div id="fvlib-fis-logo-panel"><img src="https://tracker.flightview.com/images/Logos/OAG/OAG_436x274.png" height="80"></div>
      <div class="fvlib-fis-title-panel"><span id="fvlib-fis-title"></span></div>
    </div>
    <div class="fvlib-fis-main-body">
      <div id="fvlib-fis-details">
        <div id="fvlib-fis-details-inner" style="display: none">
          <div id="fvlib-fis-status-panel" class="fvlib-fis-panel">
            <span id="fvlib-fis-status"></span>
            <span id="fvlib-fis-status-detailed"></span>
          </div>
          <div id="fvlib-fis-from-panel" class="fvlib-fis-panel">
            <span id="fvlib-fis-from-label" class="fvlib-fis-airport-detail-label"></span>
            <span id="fvlib-fis-from-airport-name" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-from-scheduled-time" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-from-actual-time" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-from-estimated-time" class="fvlib-fis-airport-detail"></span>
          </div>
          <div id="fvlib-fis-to-panel" class="fvlib-fis-panel">
            <span id="fvlib-fis-to-label" class="fvlib-fis-airport-detail-label"></span>
            <span id="fvlib-fis-to-airport-name" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-to-scheduled-time" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-to-actual-time" class="fvlib-fis-airport-detail"></span>
            <span id="fvlib-fis-to-estimated-time" class="fvlib-fis-airport-detail"></span>
          </div>
          <div id="fvlib-fis-other-info-panel" class="fvlib-fis-panel">
            <span id="fvlib-fis-aircraft-type"></span>
            <span id="fvlib-fis-ground-speed"></span>
            <span id="fvlib-fis-altitude"></span>
          </div>
        </div>
        <div id="fvlib-fis-gate-panel" class="fvlib-fis-panel">
          <span id="fvlib-fis-gate"></span>
        </div>
      </div>
      <div id="fvlib-map" class="fvlib-fis-map">
      </div>
    </div>

  </div>
</template>
`

export class FlightViewMapLibFIS {
  mapModule: FlightViewMapLibMap

  // data
  props: FISProps
  flights: FlightData[]
  flight: Flight
  airportLayer: AirportLayer
  autoUpdate = true
  retryCount = 0
  timeFormat: string = 'h:mm a'

  constructor (parentElement: HTMLElement, initProps: InitProps) {
    this.props = initProps.fis
    import('./fis.css')
    if ('content' in document.createElement('template')) {
      let template: HTMLTemplateElement = document.querySelector('#fvlib-fis-template')

      if (!template) {
        parentElement.innerHTML += defaultTemplate
        template = document.querySelector('#fvlib-fis-template')
      }

      // Clone the new row and insert it into the table
      const clone = template.content.cloneNode(true)
      parentElement.appendChild(clone)

      this.mapModule = new FlightViewMapLibMap(parentElement, initProps)
      this.flight = new Flight(this.mapModule.map, this.props.planeIconSize, null, false)
      this.airportLayer = new AirportLayer(this.mapModule.map, this.props.airportIconSize || 40)

      void this.updateFlightData().then(() => {
        this.mapModule.ready(this.flights.length > 0)
      })
    }
  }

  async fetchData (): Promise<FlightData[]> {
    try {
      const flights = await getFlight(this.props)
      this.retryCount = 0
      return flights
    } catch (err) {
      // if we don't have any data, try again more quickly
      // otherwise let's keep the current (stale) data and retry after the usual delay
      if (this.flights && this.flights.length > 0) {
        return this.flights
      } else {
        this.retryCount++

        if (this.retryCount > 10) {
          // give up in case we will never get a proper response
          return []
        } else {
          // retry until connectivity is back or server answer again
          let countdown = 10
          while (countdown !== 0) {
            this.mapModule.updateMessage('GENERIC.CANNOT_REACH_DATA_SERVER_RETRYING_IN', { remaining: countdown })
            countdown--
            await sleep(1000)
          }
          this.mapModule.updateMessage('GENERIC.CANNOT_REACH_DATA_SERVER_RETRYING')
          return await this.fetchData()
        }
      }
    }
  }

  async updateFlightData () {
    this.flights = await this.fetchData()
    if (this.flights.length === 0) return
    this.mapModule.updateLastUpdated()
    this.setAirportMarkers(this.flights[0])
    this.setTitle(this.flights[0])
    this.setDetails(this.flights[0])
    this.flight.apply(this.flights)
    if (this.flights[this.flights.length - 1].status.includes('InGate')) {
      this.autoUpdate = false
    } else {
      // schedule future update
      void sleep(40 * 1000).then(() => {
        void this.updateFlightData()
      })
    }
  }

  setAirportMarkers (flight: FlightData) {
    this.airportLayer.createAirportMarkers([flight], 'name')
  }

  setDetails (flight: FlightData) {
    this.setElementText('fvlib-fis-status', getStatusLabel(flight))
    this.setElementText('fvlib-fis-status-detailed', translateStatus(flight))
    this.setFromDetails(flight)
    this.setToDetails(flight)
    this.setAircraftType(flight)
    this.setGroundSpeed(flight)
    this.setAltitude(flight)
    this.setGate(flight)
    const inner = document.getElementById('fvlib-fis-details-inner')
    if (inner) {
      inner.style.display = null
    }
  }

  setFromDetails (flight: FlightData) {
    this.setAirportDetails(flight, 'from')
  }

  setToDetails (flight: FlightData) {
    this.setAirportDetails(flight, 'to')
  }

  setAirportDetails (flight: FlightData, side: 'from' | 'to') {
    const airport = side === 'from' ? flight.departureAirport : flight.arrivalAirport
    this.setElementText(`fvlib-fis-${side}-label`, t(side === 'from' ? 'FIS.FROM' : 'FIS.TO'))
    if (airport.stateId && airport.stateId !== '') {
      this.setElementText(`fvlib-fis-${side}-airport-name`, `${airport.name.toLowerCase()}, ${airport.stateId}`)
    } else {
      this.setElementText(`fvlib-fis-${side}-airport-name`, airport.name.toLowerCase())
    }
    this.setElementText(`fvlib-fis-${side}-scheduled-time`, t('FIS.TIME_SCHEDULED', { value: format(airport.scheduled, this.timeFormat) }))
    if (airport.actual) {
      this.setElementText(`fvlib-fis-${side}-actual-time`, t('FIS.TIME_ACTUAL', { value: format(airport.actual, this.timeFormat) }))
    } else {
      this.setElementText(`fvlib-fis-${side}-actual-time`, null)
    }
    if (airport.estimated) {
      this.setElementText(`fvlib-fis-${side}-estimated-time`, t('FIS.TIME_ESTIMATED', { value: format(airport.estimated, this.timeFormat) }))
    } else {
      this.setElementText(`fvlib-fis-${side}-estimated-time`, null)
    }
  }

  setAircraftType (flight: FlightData) {
    getAircraftName(flight.flightInfo.aircraftType).then((aircraftName) => {
      this.setElementText('fvlib-fis-aircraft-type', t('FIS.AIRCRAFT_NAME', { aircraft: aircraftName }))
    }).catch((err) => {
      console.error(err)
    })
  }

  setGroundSpeed (flight: FlightData) {
    if (flight.flightInfo.speed) {
      this.setElementText('fvlib-fis-ground-speed', t('FIS.AIRCRAFT_SPEED', { value: flight.flightInfo.speed, unit: 'mph' }))
    } else {
      this.setElementText('fvlib-fis-ground-speed', null)
    }
  }

  setAltitude (flight: FlightData) {
    if (flight.flightInfo.altitude) {
      this.setElementText('fvlib-fis-altitude', t('FIS.AIRCRAFT_ALTITUDE', { value: flight.flightInfo.altitude }))
    } else {
      this.setElementText('fvlib-fis-altitude', null)
    }
  }

  setTitle (flight: FlightData) {
    this.setElementText('fvlib-fis-title', t('FIS.INCOMING_FLIGHT', { value: `${flight.flightId.airlineName} ${flight.flightId.flightNumber}` }))
  }

  setGate (flight: FlightData) {
    this.setElementText('fvlib-fis-gate', this.props.gate || flight.arrivalAirport.gate || flight.arrivalAirport.terminal)
  }

  setElementText (elementId: string, innerText: string) {
    const element = document.getElementById(elementId)
    if (element) {
      element.innerText = innerText
    }
  }
}
